import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronLeft, ChevronRight, Loader2, User, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

const statusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  CONFIRMED: 'status.confirmed',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

type HotelBookingStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'REFUNDED';

export function HotelBookingsPage() {
  const { t, i18n } = useTranslation('hotels');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<HotelBookingStatus | undefined>(
    'travel:hotels:statusFilter',
    undefined
  );
  const limit = 10;

  const { data, isLoading, error } = trpc.hotel.listHotelBookings.useQuery({
    page,
    limit,
    status: statusFilter,
  });

  const hotelBookings = data?.hotelBookings ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as HotelBookingStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="PENDING">{t('status.pending')}</option>
          <option value="CONFIRMED">{t('status.confirmed')}</option>
          <option value="CANCELLED">{t('status.cancelled')}</option>
          <option value="REFUNDED">{t('status.refunded')}</option>
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loadingBookings')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('errorLoading')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && hotelBookings.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('noBookingsFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {statusFilter ? t('adjustFilters') : t('bookingsWillAppear')}
          </p>
        </div>
      )}

      {/* Bookings Table */}
      {!isLoading && !error && hotelBookings.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('reference')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('hotel')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('customer')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('stayDates')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('details')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{tCommon('status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {hotelBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/travel/hotels/${booking.id}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {booking.bookingItem?.booking?.bookingReference || booking.amadeusBookingId}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                          <Building2 className="text-primary h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{booking.hotelName}</p>
                          <p className="text-muted-foreground text-xs">
                            {booking.roomType || t('standardRoom')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                          <User className="text-primary h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {booking.bookingItem?.booking?.customer?.firstName}{' '}
                            {booking.bookingItem?.booking?.customer?.lastName}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {booking.bookingItem?.booking?.customer?.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="text-muted-foreground h-4 w-4" />
                        <span>
                          {formatDate(booking.checkInDate)} - {formatDate(booking.checkOutDate)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p>
                          {booking.roomCount} {t('rooms')}, {booking.guestCount} {t('guests')}
                        </p>
                        {booking.boardType && (
                          <p className="text-muted-foreground text-xs">
                            {booking.boardType.replace('_', ' ')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium ${
                          statusColors[booking.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(statusLabelKeys[booking.status] || 'status.pending') || booking.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">
                        {booking.currency} {Number(booking.sellingPrice).toFixed(2)}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {t('cost')}: {booking.currency}{' '}
                        {Number(booking.totalAmadeusPrice).toFixed(2)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {t('showingResults', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  {tCommon('next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
